"use client";
import React from "react";
import { Loader2 } from "lucide-react";
import { Textarea } from "@/tools/components/ui/textarea";
import { cn } from "@/tools/lib/utils";

type StyleMap = Record<string, string> | undefined;

function pickStyles(styles: StyleMap): React.CSSProperties {
  if (!styles) return {};
  const keys = [
    "height",
    "width",
    "backgroundColor",
    "minHeight",
    "maxWidth",
    "margin",
    "padding",
    "display",
    "flexDirection",
    "flexWrap",
    "gap",
    "alignItems",
    "flex",
    "minWidth",
    "paddingRight",
    "justifyContent",
  ] as const;
  const out: React.CSSProperties = {};
  for (const k of keys) {
    const v = styles[k];
    if (v) (out as Record<string, string>)[k] = v;
  }
  return out;
}

function BlockSpinner() {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/25"
      aria-busy
      aria-label="Loading"
    >
      <div className="h-12 w-12 rounded-full border-4 border-white border-t-[#0D908E] shadow-sm animate-spin" />
    </div>
  );
}

function SlotGenerationOverlay() {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-[11] flex items-center justify-center bg-white/35"
      aria-busy
      aria-label="Generating"
    >
      <Loader2 className="h-9 w-9 animate-spin text-[#0D908E]" aria-hidden />
    </div>
  );
}

type UiComp = {
  method?: string;
  styles?: Record<string, string>;
  mStyles?: Record<string, string>;
  options?: Record<string, unknown>;
};

type SectionNode = {
  trackId?: string;
  uiCompType?: UiComp;
  items?: SectionNode[];
  panels?: SectionNode[];
  text?: string;
  imageUrl?: string;
  imageAlt?: string;
};

export type CmsTemplateLayoutProps = {
  /** The cmsTemplateJson with content already merged into section nodes */
  data: Record<string, unknown>;
  loading?: boolean;
  showRegionLabels?: boolean;
  editable?: boolean;
  onSectionChange?: (sectionKey: string, field: string, value: string) => void;
  onItemChange?: (sectionKey: string, index: number, field: string, value: string) => void;
};

function RegionLabel({ label }: { label: string }) {
  return (
    <span className="pointer-events-none absolute left-2 top-2 z-[1] rounded bg-black/45 px-2 py-0.5 text-[11px] font-medium text-white shadow-sm">
      {label}
    </span>
  );
}

function ImageSlot({
  imageUrl,
  imageAlt,
  style,
  loading,
  label,
}: {
  imageUrl?: string;
  imageAlt?: string;
  style?: React.CSSProperties;
  loading?: boolean;
  label?: string;
}) {
  // Determine whether the template slot has an explicit fixed height or only a minHeight.
  // When a fixed height is set (e.g. "min(50vh, 520px)"), the image must fill the entire
  // slot using h-full + object-cover so it crops to the wireframe dimensions rather than
  // sizing from its intrinsic aspect ratio.
  const hasFixedHeight = Boolean(style?.height);
  return (
    <div className="relative w-full overflow-hidden rounded-md" style={style}>
      {label ? <RegionLabel label={label} /> : null}
      {imageUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={imageUrl}
          alt={imageAlt ?? ""}
          loading="lazy"
          className={
            hasFixedHeight
              ? "absolute inset-0 h-full w-full object-cover object-center"
              : "w-full object-cover object-center"
          }
          style={hasFixedHeight ? undefined : { minHeight: style?.minHeight ?? "200px" }}
        />
      ) : (
        <div
          className="w-full rounded-md bg-slate-200 flex items-center justify-center"
          style={{ minHeight: style?.minHeight ?? "200px" }}
        >
          <span className="text-xs text-slate-400">No image</span>
        </div>
      )}
      {loading ? <BlockSpinner /> : null}
    </div>
  );
}

/**
 * Render a markdown-lite text block.
 * Lines starting with ## are rendered as <h2>, # as <h1>.
 * Remaining lines are joined and rendered as body text.
 * This mirrors the HTML preview renderer in local_blog_output.py.
 */
function renderMarkdownLite(text: string, className?: string): React.ReactNode {
  const lines = text.split("\n");
  const nodes: React.ReactNode[] = [];
  const bodyLines: string[] = [];

  const flushBody = () => {
    const body = bodyLines.join("\n").trim();
    if (body) {
      nodes.push(
        <p key={`body-${nodes.length}`} className={cn("text-sm text-slate-800 leading-relaxed whitespace-pre-wrap", className)}>
          {body}
        </p>,
      );
    }
    bodyLines.length = 0;
  };

  for (const line of lines) {
    if (line.startsWith("## ")) {
      flushBody();
      nodes.push(
        <h2 key={`h2-${nodes.length}`} className="mt-4 mb-1 text-base font-semibold text-slate-900 first:mt-0">
          {line.slice(3).trim()}
        </h2>,
      );
    } else if (line.startsWith("# ")) {
      flushBody();
      nodes.push(
        <h1 key={`h1-${nodes.length}`} className="mt-4 mb-2 text-xl font-bold text-slate-900 first:mt-0">
          {line.slice(2).trim()}
        </h1>,
      );
    } else {
      bodyLines.push(line);
    }
  }
  flushBody();

  return <>{nodes}</>;
}

function TextLeaf({
  text,
  editable,
  onChange,
  className,
}: {
  text?: string;
  editable?: boolean;
  onChange?: (value: string) => void;
  className?: string;
}) {
  if (editable) {
    return (
      <Textarea
        value={text ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        className={cn(
          "min-h-[120px] w-full flex-1 resize-y text-sm leading-relaxed text-slate-800",
          "border-[#0D908E]/40 bg-white p-3 focus-visible:ring-[#0D908E]",
          className,
        )}
      />
    );
  }
  if (!text) return null;
  // Render markdown-style headings (## / #) as proper HTML elements.
  if (text.includes("\n## ") || text.startsWith("## ") || text.includes("\n# ") || text.startsWith("# ")) {
    return <div className={cn("space-y-1", className)}>{renderMarkdownLite(text, className)}</div>;
  }
  return <p className={cn("text-sm text-slate-800 whitespace-pre-wrap leading-relaxed", className)}>{text}</p>;
}

function renderContentOnImage(
  sectionKey: string,
  section: SectionNode,
  loading: boolean,
  showRegionLabels: boolean,
  editable: boolean,
  onSectionChange?: CmsTemplateLayoutProps["onSectionChange"],
): React.ReactNode {
  const st = section.uiCompType?.styles;
  const label = showRegionLabels ? (section.trackId ?? sectionKey) : undefined;
  return (
    <div key={sectionKey}>
      <ImageSlot
        imageUrl={section.imageUrl}
        imageAlt={section.imageAlt}
        style={pickStyles(st)}
        loading={loading}
        label={label}
      />
      {section.text != null && (
        <div className="mt-2">
          <TextLeaf
            text={section.text}
            editable={editable}
            onChange={(v) => onSectionChange?.(sectionKey, "text", v)}
          />
        </div>
      )}
    </div>
  );
}

function renderItemsGrid(
  sectionKey: string,
  section: SectionNode,
  loading: boolean,
  showRegionLabels: boolean,
  editable: boolean,
  onItemChange?: CmsTemplateLayoutProps["onItemChange"],
): React.ReactNode {
  const ui = section.uiCompType;
  const containerStyle = pickStyles(ui?.styles);
  const isFlex = ui?.options && (ui.options as { grid?: string }).grid === "flex";
  const items = section.items ?? [];

  const renderItem = (item: SectionNode, idx: number) => {
    const lab = showRegionLabels ? (item.trackId ?? `${sectionKey}[${idx}]`) : undefined;

    if (item.imageUrl != null) {
      return (
        <div key={item.trackId ?? idx} className="space-y-1">
          <ImageSlot
            imageUrl={item.imageUrl}
            imageAlt={item.imageAlt}
            style={pickStyles(item.uiCompType?.styles)}
            loading={loading}
            label={lab}
          />
          {item.text != null && (
            <TextLeaf
              text={item.text}
              editable={editable}
              onChange={(v) => onItemChange?.(sectionKey, idx, "text", v)}
            />
          )}
        </div>
      );
    }

    return (
      <div key={item.trackId ?? idx} className="relative overflow-hidden rounded-md">
        {lab ? <RegionLabel label={lab} /> : null}
        <TextLeaf
          text={item.text}
          editable={editable}
          onChange={(v) => onItemChange?.(sectionKey, idx, "text", v)}
          className="min-h-[60px]"
        />
        {loading ? <BlockSpinner /> : null}
      </div>
    );
  };

  if (isFlex) {
    return (
      <div key={sectionKey} className="flex w-full flex-col flex-wrap gap-4 md:flex-row" style={containerStyle}>
        {items.map((item, idx) => {
          const pct = (item.uiCompType?.options as { width?: string } | undefined)?.width;
          const flexStyle: React.CSSProperties = {
            ...(pct ? { flex: `1 1 ${pct}`, minWidth: 0 } : { flex: "1 1 48%", minWidth: 0 }),
          };
          return (
            <div key={item.trackId ?? idx} style={flexStyle}>
              {renderItem(item, idx)}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div key={sectionKey} className="grid w-full grid-cols-1 gap-4 md:grid-cols-12" style={containerStyle}>
      {items.map((item, idx) => {
        const opts = item.uiCompType?.options as { md?: number; lg?: number } | undefined;
        const md = opts?.md ?? opts?.lg ?? 12;
        const spanClass = md === 8 ? "md:col-span-8" : md === 4 ? "md:col-span-4" : "md:col-span-12";
        return (
          <div key={item.trackId ?? idx} className={spanClass}>
            {renderItem(item, idx)}
          </div>
        );
      })}
    </div>
  );
}

function renderPanels(
  sectionKey: string,
  section: SectionNode,
  loading: boolean,
  showRegionLabels: boolean,
  editable: boolean,
  onItemChange?: CmsTemplateLayoutProps["onItemChange"],
): React.ReactNode {
  const ui = section.uiCompType;
  const containerStyle = pickStyles(ui?.styles);
  const panels = section.panels ?? [];
  return (
    <div key={sectionKey} className="flex w-full flex-col flex-wrap gap-4 md:flex-row" style={containerStyle}>
      {panels.map((panel, idx) => {
        const lab = showRegionLabels ? (panel.trackId ?? `${sectionKey}[${idx}]`) : undefined;
        return (
          <div
            key={panel.trackId ?? idx}
            className="relative overflow-hidden rounded-md"
            style={{
              ...pickStyles(panel.uiCompType?.styles),
              flex: panel.uiCompType?.styles?.flex ?? "1 1 45%",
              minWidth: panel.uiCompType?.styles?.minWidth ?? "200px",
            }}
          >
            {lab ? <RegionLabel label={lab} /> : null}
            <TextLeaf
              text={panel.text}
              editable={editable}
              onChange={(v) => onItemChange?.(sectionKey, idx, "text", v)}
              className="min-h-[60px]"
            />
            {loading ? <BlockSpinner /> : null}
          </div>
        );
      })}
    </div>
  );
}

function renderSection(
  sectionKey: string,
  section: SectionNode,
  loading: boolean,
  showRegionLabels: boolean,
  editable: boolean,
  onSectionChange?: CmsTemplateLayoutProps["onSectionChange"],
  onItemChange?: CmsTemplateLayoutProps["onItemChange"],
): React.ReactNode {
  if (section.panels && section.panels.length > 0) {
    return renderPanels(sectionKey, section, loading, showRegionLabels, editable, onItemChange);
  }
  const method = section.uiCompType?.method;
  if (method === "renderContentOnImage") {
    return renderContentOnImage(sectionKey, section, loading, showRegionLabels, editable, onSectionChange);
  }
  if (method === "renderItemsGrid" && section.items && section.items.length > 0) {
    return renderItemsGrid(sectionKey, section, loading, showRegionLabels, editable, onItemChange);
  }
  // Default: text leaf (or empty placeholder when text is absent)
  if (section.text != null || editable) {
    const label = showRegionLabels ? (section.trackId ?? sectionKey) : undefined;
    return (
      <div key={sectionKey} className="relative">
        {label ? <RegionLabel label={label} /> : null}
        <TextLeaf
          text={section.text}
          editable={editable}
          onChange={(v) => onSectionChange?.(sectionKey, "text", v)}
        />
      </div>
    );
  }
  return null;
}

/**
 * Renders a CMS template JSON with content already merged into section nodes.
 * Replaces FilledWireframeArticleLayout — reads directly from section.text /
 * section.imageUrl instead of a separate slots array.
 *
 * In editable mode each text field becomes a Textarea and each imageUrl an Input.
 */
export default function CmsTemplateLayout({
  data,
  loading = false,
  showRegionLabels = false,
  editable = false,
  onSectionChange,
  onItemChange,
}: CmsTemplateLayoutProps) {
  const order = Array.isArray(data.order) ? (data.order as string[]) : [];
  const sections = (data.sections && typeof data.sections === "object"
    ? data.sections
    : {}) as Record<string, SectionNode>;

  if (order.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center">
        <p className="text-sm text-slate-400">No sections found in this blog template.</p>
      </div>
    );
  }

  return (
    <div className="relative w-full space-y-6">
      {loading && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/50">
          <SlotGenerationOverlay />
        </div>
      )}
      {order.map((key) => {
        const sec = sections[key];
        if (!sec || typeof sec !== "object") return null;
        return (
          <div key={key}>
            {renderSection(key, sec, loading, showRegionLabels, editable, onSectionChange, onItemChange)}
          </div>
        );
      })}
    </div>
  );
}
